import { Component, ComponentRef, ElementRef, ViewChild, ViewContainerRef } from '@angular/core';
import { WIZ_EVENT_TYPE, WIZ_PHYSIC_POSITION,} from '@wizeoo/akm-core-lib';
import {
  FilterComponent,
  PopUpManager,
  WizBaseCollector,
  WizBasePopUp,
  WizComponentManager,
  WizDatasource,
  WizDropdown,
  WizDropItemEvent,
  WizList,
  WizPopUp,
  WizTileList,
  WizVgroup,
  WizEventManager, WizEvent
} from '@wizeoo/akm-component-lib';
import { WidgetConfigurationObj, WidgetConfigurationItemObj, WidgetKeyPair } from '@wizeoo/akm-message-lib';
import { cloneDeep } from 'lodash-es';

import { ALK_EVENT_TYPE, CHART_WIDGET_MODE, WIDGET_KIND } from '../../../../model/enums';
import { IWidget } from '../../../../manager/widgets/i-widget';
import { AlkEvent } from '../../../../framework/events/alk.event';
import { FreedataArgsVO } from '../../../../vo/core/freedataargs.vo';
import { ChartsEngine } from '../../../../manager/charts/charts.engine';
import { FakeVO } from '../../../../vo/common/tool/fake.vo';
import { Model } from '../../../../model/model';
import { ChartEditItemRenderer } from '../renderer/chart-edit-item.renderer';
import { ChartParamRenderer } from '../renderer/chart-param.renderer';
import { ChartItemSettingPop } from './chartitem-setting.pop';
import { ChartWidget } from '../chart.widget';
import { ChartparameterObj } from '../../../wiz-ngx-charts/config/chart-parameter.obj';
import { ChartDecriptionObj } from '../../../wiz-ngx-charts/config/chart-description.obj';
import { FeiVO } from '../../../../vo/workingdata/fei.vo';
import { PivotField } from '../../../../manager/baselines/pivot-field.obj';
import { getDefaultWidgetConfigurationItemObj } from '../../../../manager/widgets/widget.engine';
import { Color, colorSets } from '../../../ngx-charts/utils/color-sets';
import { widgetHeaderVO } from '../../pivot/renderers/header/header-editext.pop';

@Component({
  // popUp d'édition des listes : gestion des emplacements de champs, formules d'aggrégats et rendu
  selector: 'chart-settings-pop',
  templateUrl: './chart-settings.pop.html',
})
export class ChartSettingsPop extends WizBasePopUp {
  // zone globale
  @ViewChild('vgDetailZone', { read: WizVgroup, static: false })
  vgDetailZone: WizVgroup;
  /** drop de choix du chart */
  @ViewChild('ddlType', { read: WizDropdown }) ddlType: WizDropdown;
  // liste des champs disponibles
  @ViewChild('fieldList', { read: WizTileList }) fieldList: WizTileList;
  @ViewChild('lstRow', { read: WizList }) lstRow: WizList; // liste des champs pour construire les lignes

  // liste des mesures disponibles
  @ViewChild('measureList', { read: WizList }) measureList: WizList;
  @ViewChild('lstData', { read: WizList }) lstData: WizList; // liste des champs de mesure (axe X)
  @ViewChild('lstColumn', { read: WizList }) lstColumn: WizList; // liste des champs de mesure (axe Y)

  @ViewChild('detailGP', { read: WizVgroup, static: false })
  detailGP: WizVgroup; // zone de détail, il faut un type de charts
  @ViewChild('dataGP', { read: WizVgroup, static: false }) dataGP: WizVgroup; // zone de définition des lignes
  @ViewChild('measureGP', { read: WizVgroup, static: false })
  measureGP: WizVgroup; // zone de choix des mesures
  @ViewChild('paramsGP', { read: WizVgroup, static: false })
  paramsGP: WizVgroup; // zone de configuration des autres params

  @ViewChild('paramsContainer', { read: ViewContainerRef, static: true })
  paramsContainer: ViewContainerRef; // zone d'ajout des paramètres

  @ViewChild('measureFreeFilter', { read: FilterComponent })
  measureFreeFilter: FilterComponent;

  // dimensions demandées
  public static width: string = '800px';
  public static height: string = 'calc(100% - 81px)';
  public popUpMode: string = WIDGET_KIND.CHART;

  /** Renderer de la liste */
  public renderer: any = ChartEditItemRenderer;
  public fieldFreeDS: WizDatasource<PivotField> = null; // DS des champs disponibles (au step)
  public measureFreeDS: WizDatasource<PivotField> = null; // DS des champs disponibles (au step)

  /** ds de la combo de choix du type de chart */
  public charTypeDS: WizDatasource<ChartDecriptionObj> = new WizDatasource<ChartDecriptionObj>(
    ChartsEngine.chartType
  ).build();
  
  public rowDS: WizDatasource<PivotField> = new WizDatasource<PivotField>().build();
  public dataDS: WizDatasource<PivotField> = new WizDatasource<PivotField>().build();
  public columnDS: WizDatasource<PivotField> = new WizDatasource<PivotField>().build();

  /** liste des champs disponibles pour la couleur */
  public colorDS: WizDatasource<FreedataArgsVO> = new WizDatasource<FreedataArgsVO>([])
    .setSortFields([{ property: 'order', way: 'asc', numeric: true }])
    .setSelectionMode('single')
    .build();
  public get useColorField(): boolean {
    if(this.rowDS?.getSource()?.length > 0) {
      return true;
    }
    return false;
  }


  protected fei: FeiVO; // les données
  protected context: WidgetConfigurationObj; // la configuration
  protected updateCallback: Function; // la callback pour retour et traitement dans le pivotComponent
  
  /** cast du data en ChartWidget */
  protected  get widget(): IWidget {
    return this.datas as ChartWidget;
  } 

  /** accesseur sur le modèle du widget */
  protected  get model(): Model {
    return this.widget.model;
  } 


  /* ##############################################################################
                  critères de restriction d'affichage
  ############################################################################## */

  private currentChartType: ChartDecriptionObj = null; // le type de chart en cours
  private paramMap: Map<string, any> = new Map<string, any>(); // les paramètres pour ce type de chart code = value
  /** contrôle des paramètres requis */
  public checkParam(paramName: string): boolean {
    return this.paramMap.has(paramName);
  }

  private measureRequired: number = 0; // nombre de champs nécessaires en mesures, selon le type de graph

  public needXDatas: boolean = false; // besoin de données en axe X (linéaire, pour lines)
  private linearRequired: number = 0; // un axe linéaire requis

  protected doSaveFlag: boolean = true; // marqueur de validation des modifs (false sur cancel)
  protected fieldPop: WizPopUp; // la popUp d'édition

  /* ##############################################################################
                  paramètres spécifiques
  ############################################################################## */

  public paramComponents: ComponentRef<ChartParamRenderer>[]; // les champs de params

  public paramsColorSchemeDS: WizDatasource<Color> = new WizDatasource<Color>(colorSets)
    .setItemCodeField('name')
    .build(); // jeux de couleur
  public paramsLegendPositionDS: WizDatasource<any> = new WizDatasource<FakeVO>(
    ChartsEngine.chartLegendPositionList
  ).build(); // emplacement de la légende

  /* ##############################################################################
                  cycle de vie principal
  ############################################################################## */

  constructor(public elementRef: ElementRef) {
    super();
  }

  show(): void {
    this.fei = this.widget.fei;
    this.context = cloneDeep(this.widget.context);
    this.updateCallback = this.widget.backFromSettingsPop.bind(this.widget);


    // creation des capsules
    let prototypes: Map<string,PivotField> = new Map();
    let piv: PivotField;
    this.fei.keys.forEach(fkd => {
      piv = new PivotField(this.widget, null, fkd, null);
      piv.popUpMode = this.popUpMode;
      prototypes.set(piv.fullCode, piv);
    });

    // Configuration du filtre de la DS measureFreeDS
    this.measureFreeFilter.configure(['code', 'fullCode', 'labelOrDefaultLabel']).build();

    this.fieldFreeDS = new WizDatasource<PivotField>(Array.from(prototypes.values()))//.filter((item) => item.isKey))
      .setItemCodeField('fullCode')
      .setSelectionMode('none')
      .build();

    this.measureFreeDS = new WizDatasource<PivotField>(
      Array.from(prototypes.values()).filter((item) => this.model.dataEngine.datakindIsNumeric(item.fkd.datakind))
    )
      .setItemCodeField('fullCode')
      .setSelectionMode('none')
      .setFilter(this._measureFreeDSFilterFn.bind(this))
      .build();

    // alimentation des collections depuis le contexte
    const preRowArr: PivotField[] = [];
    const preColsArr: PivotField[] = [];
    const preDataArr: PivotField[] = [];

    let pv: PivotField;
    let prototype: PivotField;
    this.context.r.forEach((confItem: WidgetConfigurationItemObj) => {
      
      prototype = prototypes.get(confItem.cod);
      if (!!prototype) {
        pv = new PivotField(this.widget, confItem, prototype.fkd, null );
        pv.usedAsRow = true;
        pv.pinned = confItem.pin; // reprise des informations de pin
        pv.popUpMode = this.popUpMode;
        preRowArr.push(pv);
      }
    });
    this.context.d.forEach((confItem: WidgetConfigurationItemObj) => {
      prototype = prototypes.get(confItem.cod);
      if (!!prototype) {
        pv = new PivotField(this.widget, confItem, prototype.fkd, null);
        pv.usedAsData = true;
        pv.popUpMode = this.popUpMode;
        preDataArr.push(pv);
      }

    });

    this.context.c.forEach((confItem: WidgetConfigurationItemObj) => {
      prototype = prototypes.get(confItem.cod);
      if (!!prototype) {
        pv = new PivotField(this.widget, confItem, prototype.fkd, null);
        pv.usedAsColumn = true;
        pv.popUpMode = this.popUpMode;
        preColsArr.push(pv);
      }
    });

    this.rowDS.setSource(preRowArr);
    this.dataDS.setSource(preDataArr);
    this.columnDS.setSource(preColsArr);

    this.feedMain();
    this.feedColorDS();
    this.addListeners();
  }

  /** sur séquence de démarrage, alimentation des listes avec leurs DS */
  public feedMain(): void {
    // aucun contrôle sur rows et datas
    this.lstRow.wizDataProvider = this.rowDS;
    this.lstData.wizDataProvider = this.dataDS;
    this.lstColumn.wizDataProvider = this.columnDS;

    this.lstRow.dropControlFunction = this.RowDropControl.bind(this);
    this.lstData.dropControlFunction = this.dataDropControl.bind(this);
    this.lstColumn.dropControlFunction = this.dataYDropControl.bind(this);

    // Sélection du type de chart déjà configuré sur le widget
    this.currentChartType = ChartsEngine.chartType.find((t) => t.code === this.context.ct);
    this.charTypeDS.select([this.currentChartType]);

    this.feedChartType(); // on délègue pour le cycle de vie avec type de chart
  }

  private feedColorDS(): void {
    
    if(this.rowDS.getWorkedSource().length > 0)  {
      let pv: PivotField = this.rowDS.getWorkedSource()[0];
      const argSrc: FreedataArgsVO[] = this.model.freedataEngine.getArgsFromFD(pv.fkd.code);
      this.colorDS.setSource(argSrc ? [...argSrc] : []);

      if(this.context.f?.length > 0) {
        let colCode: string = this.context.f[0].cod;
        let arg: FreedataArgsVO = this.colorDS.getItemByKey(colCode);
        this.colorDS.select([arg]);
      }

    } else {
      this.colorDS.setSource([]);
    }
  }


  /** mise en place des écouteurs */
  public addListeners(): void {
    // écouteur global change pour modelUpdate
    WizEventManager.addListener(this, this.vgDetailZone, WIZ_EVENT_TYPE.DRAG_DROPPED, this.updateModel);
    WizEventManager.addListener(this, this.vgDetailZone, WIZ_EVENT_TYPE.DRAG_MOVED, this.updateModel);
    WizEventManager.addListener(this, this.vgDetailZone, WIZ_EVENT_TYPE.DELETE, this.updateModel);
    WizEventManager.addListener(this, this.ddlType, WIZ_EVENT_TYPE.SELECTION_CHANGED, this.feedChartType);

    WizEventManager.addListener(this, this.paramsGP, ALK_EVENT_TYPE.CHART_UPDATE_PARAMETER, this.refreshParams); // conf params
    WizEventManager.addListener(this, this.elementRef, ALK_EVENT_TYPE.PIVOT_CONFIGURE_FIELD, this.showConfPop); // demande de conf d'un champ

    WizEventManager.addListener(this, this.measureFreeFilter, WIZ_EVENT_TYPE.FILTER, this._onMeasureFreeDSFilter);
  }

  //---- Filter Function sur les champs de mesure ---//
  private _onMeasureFreeDSFilter() {
    this.measureFreeDS.refresh();
  }

  private _measureFreeDSFilterFn(item: PivotField): boolean {
    return this.measureFreeFilter.checkVisible(item);
  }

  /* ##############################################################################
                  cycle de vie avec type de graphique
  ############################################################################## */

  /** modification de la popUp de choix des charts */
  private feedChartType(e: WizEvent = null): void {
    this.context.ct = this.charTypeDS.selectedItem.code;
    this.currentChartType = this.charTypeDS.selectedItem;
    this.paramMap = this.currentChartType.paramMap;

    Array.from(this.paramMap.keys()).forEach((cod: string) => {
      // on rince la map de params
      this.paramMap.set(cod, null);
    });

    // reprise des données du contexte
    // console.log('chart conf', this.context.cp);
    this.context.cp.forEach((confItem: WidgetKeyPair) => {
      // console.log('chart confItem', confItem);
      
      if (this.paramMap.has(confItem.cod)) {
        // console.log('chart confItem ok', confItem.val);
        this.paramMap.set(confItem.cod, confItem.val);
      }
    });

    this.needXDatas = this.currentChartType.linearAxe > 0;
    this.measureRequired = this.currentChartType.series > 0 ? this.currentChartType.series : 5;
    this.linearRequired = this.currentChartType.linearAxe;

    this.feedDetail(); // alimentation du détail
  }

  /** suppression des params */
  private clearParamCompos(): void {
    this.paramsContainer.clear(); // vider le container des params
    this.paramComponents = []; // on rince la référence aux params
  }

  /** construction des params */
  protected buildParamCompos() {
    this.clearParamCompos();

    const allParams: ChartparameterObj[] = ChartsEngine.chartParameterList;
    allParams.forEach((cpo: ChartparameterObj) => {
      if (this.paramMap.has(cpo.code)) {
        // const cpoEnable: boolean = !cpo.dependency || (this.paramMap.has(cpo.dependency) && !!this.paramMap.get(cpo.dependency));
        cpo.value = this.paramMap.get(cpo.code) !== null ? this.paramMap.get(cpo.code) : cpo.defaultValue;
        this.paramMap.set(cpo.code, cpo.value); // alimentation de la map avec les valeurs par défaut (éventuellement)

        const meCompo: ComponentRef<ChartParamRenderer> = WizComponentManager.instance.addChildTo(
          ChartParamRenderer,
          this.paramsContainer,
          {
            instanceProperties: {
              data: cpo,
              enabled: this.checkCompDependency(cpo),
            },
          }
        );
        this.paramComponents.push(meCompo);
      }
    });
  }

  private checkCompDependency(cpo: ChartparameterObj): boolean {
    let cpoEnable: boolean = true;
    if (!!cpo.dependency) {
      const masterVal: any = this.paramMap?.get(cpo.dependency) ? JSON.parse(this.paramMap.get(cpo.dependency)) : null;
      if (!masterVal) {
        cpoEnable = false;
      }
    }
    return cpoEnable;
  }

  /** sur choix / changement de type de chart, alimentation du détail et contrôle de cohérence des mesures */
  public feedDetail(): void {
    this.checkRequired(); // contrôle de cohérence
    this.buildParamCompos();
  }

  /** définition du nombre de champs requis en mesure */
  private checkRequired(): void {
    if (this.dataDS.getSource().length > this.measureRequired) {
      const newSource: PivotField[] = this.dataDS.getSource().slice(0, this.measureRequired);
      this.dataDS.setSource(newSource);
    }

    if (this.columnDS.getSource().length > this.linearRequired) {
      const newSource: PivotField[] = this.columnDS.getSource().slice(0, this.linearRequired);
      this.columnDS.setSource(newSource);
    }
  }

  public refreshParams(e: AlkEvent): void {
    const cpo: ChartParamRenderer = e.data;
    // console.log('refreshP', cpo.data.code, cpo.data.value);
    this.paramMap.set(cpo.data.code, cpo.data.value);
    this.checkCompoEnable();
  }

  private checkCompoEnable(): void {
    this.paramComponents.forEach((cr: ComponentRef<ChartParamRenderer>) => {
      const inst: ChartParamRenderer = cr.instance;
      inst.enabled = this.checkCompDependency(inst.data);
    });
  }

  /**
   * demande d'affichage de la popUp de configuration des champs
   * @param e alkEvent, data = [pivotField, wizBaseCollector]
   */
  protected showConfPop(e: AlkEvent): void {
    if (PopUpManager.exist(this.fieldPop)) {
      // suppression de l'instance existante
      this.fieldPop.hide();
    }

    // ouverture de la pop. Data[fei, context, WizbaseCollector, PivotField, callback]
    this.fieldPop = new WizPopUp(
      ChartItemSettingPop,
      true,
      false,
      WIZ_PHYSIC_POSITION.MIDDLE_CENTER,
      ChartItemSettingPop.baseWidth + 'px',
      ChartItemSettingPop.baseHeight + 'px'
    );

    // construction des données
    const datas: widgetHeaderVO = {
      popReference: this.fieldPop,
      fei: this.fei,
      context: this.context,
      updateCallback: this.backFromFieldPop.bind(this),
      pivotField: e.data[0],
      baseCollector: e.data[1],
      widget: this.widget,
      popUpMode: WIDGET_KIND.CHART
    };

    // alimentation des datas
    this.fieldPop.datas = datas;

    this.fieldPop.show();
  }

  /**
   * retour d'édition de champ
   * @param wbc la collection d'origine, transmise par l'event de showConfPop
   * @param pf PivotField
   */
  protected backFromFieldPop(wbc: WizBaseCollector, pf: PivotField): void {
    wbc.wizDataProvider.refresh();
    // console.log('back from pop Edit');
  }

  /* ##############################################################################
                  contrôle du drag n drop
  ############################################################################## */

  /** Fonction de controle du drop sur les clés d'aggregat : unicité sur row/column */
  public RowDropControl({ item, dragSource }: WizDropItemEvent): boolean {
    const field: PivotField = item.data as PivotField;
    // if (!field.isKey) {
    //   // uniquement les Keys en aggrégat (pas les filters, juste les Keys)
    //   return false;
    // }

    // seulement pour les pkd ou origine = filtre. Sinon, simple déplacement de rows vers cols
    if (dragSource === this.fieldList) {
      for (const pkd of this.rowDS.getSource()) {
        if (pkd.fullCode === field.fullCode) {
          return false;
        }
      }
    }
    return true;
  }

  /** Fonction de controle du drop sur les datas : uniquement des mesures */
  public dataDropControl({ item }: WizDropItemEvent): boolean {
    const pivotField: PivotField = item.data as PivotField;
    if (!this.model.dataEngine.datakindIsNumeric(pivotField.fkd.datakind)) {
      return false;
    }
    if (this.dataDS.getSource().length >= this.measureRequired) {
      // données en X
      return false;
    }
    return true;
  }

  /** Fonction de controle du drop sur les datas (axe Y) : uniquement des mesures */
  public dataYDropControl({ item }: WizDropItemEvent): boolean {
    const pivotField: PivotField = item.data as PivotField;
    if (!this.model.dataEngine.datakindIsNumeric(pivotField.fkd.datakind)) {
      return false;
    }
    if (this.columnDS.getSource().length >= this.linearRequired) {
      // données en Y
      return false;
    }
    return true;
  }


  /* ##############################################################################
                  mise à jour sur update & génération du contexte
  ############################################################################## */

  /**
   * Modification du modèle
   * @param e Evenement model change
   */
  protected updateModel(e: WizEvent): void {
    this.checkFieldIsUsed(); // synchro du rendu de la liste principale (icone oeil pour Used)
  }

  /**
   * détection de la position des fields. Alimentation de la donnée sur dataSource (celle du FEI)
   * pour signaler les champs utilisés
   */
  protected checkFieldIsUsed(): void {
    // map pour les 2 collections
    const rowMap: Map<string, PivotField> = new Map(this.rowDS.getSource().map((item) => [item.fullCode, item]));
    const columnMap: Map<string, PivotField> = new Map(this.dataDS.getSource().map((item) => [item.fullCode, item]));
    // TODO TODO 
    this.fieldFreeDS.getSource().forEach((item: PivotField) => {
      item.usedAsRow = rowMap.has(item.fullCode);
    });
    this.fieldFreeDS.refresh();

    this.fieldFreeDS.getSource().forEach((item: PivotField) => {
      item.usedAsColumn = columnMap.has(item.fullCode);
    });
    this.fieldFreeDS.refresh();

    // refresh de la drop de couleur
    this.feedColorDS();

  }

  /** validation du contenu */
  public doValid(): void {
    this.hide();
  }

  /** annulation des modifs */
  public doCancel(): void {
    this.doSaveFlag = false;
    this.hide();
  }

  /** fermeture, avec ou sans valisation */
  public onHide() {
    if (this.doSaveFlag) {
      if (!!this.updateCallback) {
        this.updateContext();
        this.updateCallback(this.context);
      }
    }
  }

  public get isValidable(): boolean {
    let validable = true;
    if (this.charTypeDS.selectedItem?.code === CHART_WIDGET_MODE.MULTI_LINE) {
      validable = this.rowDS.length > 0 && this.columnDS.length > 0 && this.dataDS.length > 0;
    }

    return validable && this.doSaveFlag;
  }

  /** modification du context pour retour sur la base des params */
  public updateContext(): void {
    this.context.r = [];
    this.context.c = [];
    this.context.d = [];
    this.context.f = []; // recyclage du filtre pour gestion du forçage de la couleur
    
    let idx: number = 1; // positions
    let confItem: WidgetConfigurationItemObj;
    this.rowDS.getSource().forEach((item: PivotField) => { // les entrées en ligne
      // rows
      confItem = this.model.widgetEngine.newWcio(item.fullCode, idx);
      confItem.lbl = item.labelOrDefaultLabel;
      confItem.fdvf = item.fdvField;
      this.context.r.push(confItem);
      idx++;
    });

    this.dataDS.getSource().forEach((item: PivotField) => { // Datas 'axe Y)  
      confItem = this.model.widgetEngine.newWcio(item.fullCode, idx);
      confItem.agg = item.aggOrDefaultFunc();
      confItem.lbl = item.labelOrDefaultLabel;
      confItem.fdvf = item.fdvField;
      this.model.widgetEngine.wcioStyleParams(confItem, item.format, item.style); // format & style
      
      this.context.d.push(confItem);
      idx++;
    });

    this.columnDS.getSource().forEach((item: PivotField) => { // axe X
      confItem = this.model.widgetEngine.newWcio(item.fullCode, idx);
      confItem.lbl = item.labelOrDefaultLabel;
      confItem.fdvf = item.fdvField;
      this.model.widgetEngine.wcioStyleParams(confItem, item.format); // format & style

      this.context.c.push(confItem);
      idx++;
    });

    this.context.cp = [];
    let chartConfItem: WidgetKeyPair;
    Array.from(this.paramMap.keys()).forEach((key: string) => {
      chartConfItem =  {cod: key, val:this.paramMap.get(key)} ; 
      // console.log('update', key, this.paramMap.get(key));
      this.context.cp.push(chartConfItem);
    });

    // forçage de la couleur
    // recyclage du champ filter pour mémoriser le forçage de la couleur...
    if(this.colorDS?.selectedItem) {
      let wcio: WidgetConfigurationItemObj = getDefaultWidgetConfigurationItemObj();
      wcio.cod = this.colorDS.selectedItem.code;
      this.context.f.push(wcio);
    } 
  }
}
